import React, { useState } from 'react';
import { Card, CardContent, CardActions, Typography, Stack, Button, Collapse, Box, Divider } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

interface RelationalCardProps {
    icon: React.ReactNode;
    label: string;
    updatedAt?: string;
    children?: React.ReactNode;
    length: number;
    isDraggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
    actions?: React.ReactNode;
    expanded?: boolean;
    type: string;
    onExpandChange?: (expanded: boolean) => void;
}

const RelationalCard: React.FC<RelationalCardProps> = ({
    label,
    updatedAt,
    children,
    length,
    isDraggable,
    onDragStart,
    onDragEnd,
    actions,
    expanded,
    type,
    onExpandChange,
}) => {
    const [internalExpanded, setInternalExpanded] = useState(false);
    const isSchedulePlan = type === 'schedulePlan';
    expanded = isSchedulePlan ? true : expanded;
    const isControlled = expanded !== undefined;
    const actualExpanded = isControlled ? expanded : internalExpanded;

    const handleExpandClick = () => {
        if (isControlled && onExpandChange) {
            onExpandChange(!expanded);
        } else {
            setInternalExpanded((e) => !e);
        }
    };

    return (
        <Card
            variant="outlined"
            sx={{
                minWidth: 120,
                width: '100%',
                borderRadius: 3,
                boxShadow: 1,
                cursor: isDraggable ? 'grab' : 'default',
                opacity: isDraggable ? 0.95 : 1,
                position: 'relative',
            }}
            draggable={isDraggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
        >
            <CardContent sx={{ p: 1 }}>
                <Stack spacing={0}>
                    {actions && <Box sx={{ zIndex: 2, alignSelf: 'flex-end' }}>{actions}</Box>}
                    <Stack spacing={1}>
                        <Typography variant="body1" lineHeight={1.25} fontWeight={600} title={label}>
                            {label}
                        </Typography>
                        {updatedAt && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {new Date(updatedAt).toLocaleDateString()}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                <Collapse in={actualExpanded}>
                    <Divider sx={{ my: 1 }} />
                    {children}
                </Collapse>
            </CardContent>
            {!isSchedulePlan && (
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button
                        size="small"
                        onClick={handleExpandClick}
                        sx={{ color: length > 0 ? 'primary.main' : 'text.secondary' }}
                        endIcon={actualExpanded ? <ExpandLess /> : <ExpandMore />}
                    >
                        {actualExpanded ? 'Hide Progress' : 'Show Progress'}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
};

export default RelationalCard;
